import type { KametiGroup } from "../services/types";

/**
 * Collection arithmetic. Demands are derived from closed cycles (the same
 * figures the auction module computed) and paid state is aggregated from
 * receipts. These helpers keep the derivation and the late-fee policy in one
 * place so every collection screen agrees. All money uses Indian lakh grouping.
 */

const ONES = [
  "Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen", "Eighteen", "Nineteen",
];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function twoDigits(n: number): string {
  if (n < 20) return ONES[n];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return TENS[t] + (o ? ` ${ONES[o]}` : "");
}

function threeDigits(n: number): string {
  const h = Math.floor(n / 100);
  const r = n % 100;
  let s = "";
  if (h) s += `${ONES[h]} Hundred`;
  if (r) s += (s ? " " : "") + twoDigits(r);
  return s;
}

/**
 * Convert an amount into Indian English words, e.g. ₹85,000.00 →
 * "Eighty-Five Thousand Rupees Only". Receipts in this market expect the amount
 * spelled out, and paise are included when present.
 */
export function amountInWords(amount: number): string {
  if (!Number.isFinite(amount) || amount < 0) return "Zero Rupees Only";
  const whole = Math.floor(amount);
  const paise = Math.round((amount - whole) * 100);
  let n = whole;
  const crore = Math.floor(n / 10000000);
  n %= 10000000;
  const lakh = Math.floor(n / 100000);
  n %= 100000;
  const thousand = Math.floor(n / 1000);
  n %= 1000;
  const parts: string[] = [];
  if (crore) parts.push(`${threeDigits(crore)} Crore`);
  if (lakh) parts.push(`${twoDigits(lakh)} Lakh`);
  if (thousand) parts.push(`${twoDigits(thousand)} Thousand`);
  if (n) parts.push(threeDigits(n));
  let words = parts.length ? parts.join(" ") : "Zero";
  words += " Rupees";
  if (paise > 0) words += ` and ${twoDigits(paise)} Paise`;
  return `${words} Only`;
}

/** Flat cap applied to per-day late-fee accumulation for display sanity. */
const MAX_OVERDUE_DAYS_FOR_FEE = 30;

/**
 * Compute the late fee that applies to a demand under a group's policy.
 * The grace period is handled by the caller (a demand is only overdue once past
 * due + grace). Per-day fees accumulate per overdue day, capped for display.
 */
export function computeLateFee(
  group: Pick<KametiGroup, "lateFee" | "lateFeeIsPercent" | "lateFeePerDay">,
  netInstallment: number,
  daysOverdue: number
): number {
  if (!group || group.lateFee <= 0 || daysOverdue <= 0 || netInstallment <= 0) return 0;
  const safeLateFee = Math.max(0, group.lateFee);
  const base = group.lateFeeIsPercent ? (netInstallment * safeLateFee) / 100 : safeLateFee;
  if (!group.lateFeePerDay) return Math.max(0, base);
  const days = Math.max(1, Math.min(daysOverdue, MAX_OVERDUE_DAYS_FOR_FEE));
  return Math.max(0, base * days);
}

/** Date of a given day-of-month within a cycle's month (days capped at 28). */
export function cycleDate(startDate: string, cycle: number, day: number): Date {
  const d = new Date(startDate);
  const total = d.getMonth() + (cycle - 1);
  const year = d.getFullYear() + Math.floor(total / 12);
  return new Date(year, total % 12, Math.min(Math.max(day, 1), 28));
}